import React, { useEffect, useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import IconButton from "../ui/Button/IconButton";
import useAppStore from "../../Store/SearchStore";
import { encodeSearchText } from "../../Utils/urlEncoder.js";
import Cookies from "js-cookie";

const RecordButton = ({ pageType, onRecordingStateChange }) => {
  const { t } = useTranslation();
  const history = useHistory();
  
  const {
    searchText,
    setSearchText,
    getWantedGifts,
    setIsVoiceRecognitionActive,
  } = useAppStore();

  const [recordingState, setRecordingState] = useState("idle");
  const [isSupported, setIsSupported] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isHolding, setIsHolding] = useState(false);

  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const originalTextRef = useRef("");
  const holdTimeoutRef = useRef(null);
  const buttonRef = useRef(null);
  const restartTimeoutRef = useRef(null);

  const LONG_PRESS_DURATION = 200;
  const MAX_RECORDING_DURATION = 30000;

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      setRecordingState("unsupported");
      setErrorMessage(t("voiceSearch.notSupported"));
    }

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (restartTimeoutRef.current) clearTimeout(restartTimeoutRef.current);
    };
  }, [t]);

  useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(recordingState);
    }
  }, [recordingState, onRecordingStateChange]);

  const performSearch = useCallback(async (text) => {
    if (!text || text.trim() === "") return;
    
    setRecordingState("processing");
    setSearchText(text);
    
    const cookie = Cookies.get();
    await getWantedGifts(cookie, true);
    
    const encodedSearchText = encodeSearchText(text);
    history.push(`/search?query=${encodedSearchText}`);
    setRecordingState("idle");
  }, [getWantedGifts, history, setSearchText]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setRecordingState("error");
    setSearchText(originalTextRef.current);
    setIsHolding(false);
    
    setTimeout(() => {
      setRecordingState("idle");
      setErrorMessage("");
    }, 3000);
  }, [setSearchText]);

  const restartRecognition = useCallback(() => {
    if (!isSupported || !recognitionRef.current) return;
    
    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Error restarting recognition:", error);
    }
  }, [isSupported]);

  const startRecognition = useCallback(() => {
    if (recordingState === "listening" || !isSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showError(t("voiceSearch.notSupported"));
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = navigator.language || "en-US";
      recognition.maxAlternatives = 1;

      originalTextRef.current = searchText;
      finalTranscriptRef.current = "";

      recognition.onstart = () => {
        setRecordingState("listening");
        setErrorMessage("");
        setIsVoiceRecognitionActive(true);
        
        timeoutRef.current = setTimeout(() => {
          if (recognitionRef.current && isHolding) {
            recognitionRef.current.stop();
          }
        }, MAX_RECORDING_DURATION);
      };

      recognition.onresult = (event) => {
        let interim = "";
        let final = "";
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + " ";
          } else {
            interim += transcript;
          }
        }

        const prefix = originalTextRef.current ? originalTextRef.current.trim() + " " : "";
        const combinedFinal = (finalTranscriptRef.current + final).trim();
        const displayText = (prefix + combinedFinal + interim).trim();
        
        setSearchText(displayText);

        if (final) {
          finalTranscriptRef.current = combinedFinal;
        }
      };

      recognition.onend = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (isHolding) {
          restartTimeoutRef.current = setTimeout(() => {
            restartRecognition();
          }, 100);
        } else {
          setIsVoiceRecognitionActive(false);
          
          const voiceText = finalTranscriptRef.current.trim();
          if (voiceText) {
            const combinedText = originalTextRef.current
              ? originalTextRef.current.trim() + " " + voiceText
              : voiceText;
            performSearch(combinedText);
          } else {
            setSearchText(originalTextRef.current);
            setRecordingState("idle");
          }
        }
      };

      recognition.onerror = (event) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (event.error === "no-speech" && isHolding) {
          restartTimeoutRef.current = setTimeout(() => {
            restartRecognition();
          }, 100);
          return;
        }

        setIsVoiceRecognitionActive(false);
        setIsHolding(false);

        switch (event.error) {
          case "audio-capture":
            showError(t("voiceSearch.micNotAccessible"));
            break;
          case "not-allowed":
            showError(t("voiceSearch.permissionDenied"));
            break;
          case "network":
            showError(t("voiceSearch.networkError"));
            break;
          case "aborted":
            break;
          default:
            showError(t("voiceSearch.genericError"));
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error("Error starting recognition:", error);
      showError(t("voiceSearch.startFailed"));
    }
  }, [recordingState, isSupported, searchText, showError, t, setSearchText, performSearch, setIsVoiceRecognitionActive, isHolding, restartRecognition]);

  const stopRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }
    setIsHolding(false);
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isSupported || recordingState === "processing") return;
    
    holdTimeoutRef.current = setTimeout(() => {
      setIsHolding(true);
      if (recordingState !== "listening") {
        startRecognition();
      }
    }, LONG_PRESS_DURATION);
  }, [isSupported, recordingState, startRecognition]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    
    if (recordingState === "listening") {
      stopRecognition();
    }
  }, [recordingState, stopRecognition]);

  const handleMouseLeave = useCallback((e) => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    
    if (recordingState === "listening") {
      stopRecognition();
    }
  }, [recordingState, stopRecognition]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    handleMouseDown(e);
  }, [handleMouseDown]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    handleMouseUp(e);
  }, [handleMouseUp]);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isSupported || recordingState === "processing") return;
    
    if (recordingState === "listening") {
      stopRecognition();
    } else if (recordingState === "idle") {
      startRecognition();
    }
  }, [recordingState, isSupported, stopRecognition, startRecognition]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick({ preventDefault: () => {}, stopPropagation: () => {} });
    }
  };

  const getIcon = () => {
    if (recordingState === "listening") {
      return (
        <div className="relative flex items-center justify-center">
          <Mic className="w-5 h-5 text-red-500" strokeWidth={2.5} />
        </div>
      );
    }
    if (recordingState === "processing") {
      return <Loader2 className="w-4 h-4 text-teal-600 animate-spin" strokeWidth={2.5} />;
    }
    if (recordingState === "unsupported" || recordingState === "error") {
      return <MicOff className="w-4 h-4 text-gray-400" strokeWidth={2.5} />;
    }
    return <Mic className="w-4 h-4 text-red-500" strokeWidth={2.5} />;
  };

  const getTooltipText = () => {
    switch (recordingState) {
      case "listening": return isHolding ? t("voiceSearch.releaseToStop") : t("voiceSearch.listening");
      case "processing": return t("voiceSearch.processing");
      case "error": return errorMessage || t("voiceSearch.errorOccurred");
      case "unsupported": return t("voiceSearch.notSupported");
      default: return t("voiceSearch.voiceSearch");
    }
  };

  const getButtonClassName = () => {
    const base = "!w-10 !h-10 !bg-white !border !border-red-500 !rounded-full !p-0 !transition-all !duration-200";
    
    let stateStyles = "";
    if (recordingState === "listening") {
      stateStyles = "!bg-red-50 !ring-2 !ring-red-500/50";
    } else if (recordingState === "processing") {
      stateStyles = "!bg-teal-50";
    } else {
      stateStyles = "hover:!bg-red-50 active:!scale-95";
    }
    
    const disabled = (!isSupported || recordingState === "processing") ? "opacity-50 cursor-not-allowed" : "";
    
    return `${base} ${stateStyles} ${disabled}`;
  };

  return (
    <div 
      className="relative inline-flex items-center justify-center"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <IconButton
        ref={buttonRef}
        icon={getIcon()}
        variant="ghost"
        size="md"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={!isSupported || recordingState === "processing"}
        tooltip={getTooltipText()}
        tooltipPosition="top"
        tooltipTheme="green-tooltip"
        className={getButtonClassName()}
        aria-label={getTooltipText()}
        aria-pressed={recordingState === "listening"}
        role="button"
        tabIndex={0}
      />
      {recordingState === "listening" && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full shadow-lg whitespace-nowrap animate-in fade-in zoom-in duration-200">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          {isHolding ? t("voiceSearch.releaseToStop") : t("voiceSearch.listening")}
        </div>
      )}
      {recordingState === "listening" && (
        <div className="absolute inset-0 rounded-full animate-ping bg-red-400/30 pointer-events-none" />
      )}
    </div>
  );
};

export default RecordButton;
